import { ENTER, TAB } from '@angular/cdk/keycodes';
import { Component, input, OnChanges, OnDestroy, OnInit, output, ViewChild } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { MatChipGrid, MatChipInputEvent } from '@angular/material/chips';
import { translate } from '@jsverse/transloco';
import { Subscription } from 'rxjs';

import { ConfigurationUtils } from 'app/utils/configuration.utils';
import { SemantriaConfiguration } from '../../../../providers/semantria-api/configuration.service';
import { AlertService } from '../../../../services/alert.service';

@Component({
  selector: 'app-tag-editor',
  templateUrl: './tag-editor.component.html',
  styleUrls: ['./tag-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: TagEditorComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: TagEditorComponent,
    },
  ],
  standalone: false,
})
export class TagEditorComponent
  implements OnInit, OnChanges, OnDestroy, ControlValueAccessor, Validator
{
  readonly configuration = input<SemantriaConfiguration>();
  readonly tags = input<string[]>();
  readonly editable = input<boolean>();
  readonly removable = input<boolean>();
  readonly showCount = input<boolean>();

  @ViewChild('tagChipList') chipList!: MatChipGrid;

  readonly tagAdded = output<string>();
  readonly tagRemoved = output<number>();

  readonly tagSeparatorKeysCodes: number[] = [ENTER, TAB];
  newChipInput = new UntypedFormControl('');
  valueChangeSub: Subscription;
  tagPlaceholder: string;
  tagValue = '';
  configurationTags: string[];

  isDisabled = false;
  isTouched = false;

  hasEieTag;
  eieTagText;
  eieTagTooltipText;

  error = '';

  onChange = (_value: string) => {};
  onTouched = () => {};

  constructor(private alertService: AlertService) {}

  ngOnInit(): void {
    this.configurationTags = this.configuration()?.tags || this.tags();
    this.valueChangeSub = this.newChipInput.valueChanges.subscribe(() => {
      this.markAsTouched();
      this.onChange(this.newChipInput.value);
    });
  }

  ngOnChanges(changes) {
    if (changes.configuration && changes.configuration.currentValue) {
      this.configurationTags = this.configuration().tags;
      this.hasEieTag = ConfigurationUtils.hasEieTag(this.configuration());
      this.eieTagText = ConfigurationUtils.getEieTagText(this.configuration());
      this.eieTagTooltipText = ConfigurationUtils.getEieTagTooltipText(this.configuration());

      if ((!this.configuration().tags || !this.configuration().tags.length) && !this.editable()) {
        this.tagPlaceholder = translate('configurations.tags.no-tags');
        this.tagValue = this.tagPlaceholder;
      } else if (!this.configuration().removed && this.editable()) {
        this.tagPlaceholder = translate('configurations.tags.enter-tag');
        this.tagValue = '';
      } else {
        this.tagPlaceholder = '';
        this.tagValue = '';
      }
    }

    if (changes.tags && changes.tags.currentValue) {
      this.configurationTags = this.tags();
      this.tagPlaceholder = translate('configurations.tags.enter-tag');
      this.tagValue = '';
    }
  }

  ngOnDestroy(): void {
    this.valueChangeSub?.unsubscribe();
  }

  public validTag(tag: string): boolean {
    return (tag || '').trim() && !/[^a-zA-Z0-9_-]/.test(tag);
  }

  public addTag(event: MatChipInputEvent): boolean {
    const newTag = event.value;

    if (!this.validTag(newTag)) {
      this.alertService.showToast(translate('configurations.tags.tag-invalid'));
      return false;
    }

    if (!this.configurationTags || !this.configurationTags.includes(newTag)) {
      this.tagAdded.emit(newTag);
    }

    this.newChipInput.setValue('');
  }

  public removeTag(i) {
    this.tagRemoved.emit(i);
  }

  public markAsTouched() {
    if (!this.isTouched) {
      this.onTouched();
      this.isTouched = true;
    }
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.newChipInput.disable();
    } else {
      this.newChipInput.enable();
    }
  }

  public writeValue(tagValue: string): void {
    this.newChipInput.setValue(tagValue);
  }

  public validate(control: AbstractControl): ValidationErrors | null {
    if (!this.chipList) {
      return null;
    }

    if (control.value === null || control.value === '') {
      this.chipList.errorState = false;
      this.error = '';
      return null;
    }

    this.chipList.errorState = true;
    this.error = translate('configurations.tags.tag-incomplete');
    return {
      incompleteChip: true,
    };
  }
}
